import { COUNTRY_CODES, COUNTRY_GROUPS, countryOf } from '@urbanrats/contracts';

/**
 * Allowed-countries field: the ISO codes as text (space or comma separated) plus quick picks by region and
 * a readable summary (names from the browser, unknown codes in red). The server checks the same ISO list.
 */
const names = (() => {
  try {
    return new Intl.DisplayNames(['en'], { type: 'region' });
  } catch {
    return null;
  }
})();
export const countryName = (c: string) => names?.of(c) ?? c;

const parse = (v: string) => [...new Set(v.toUpperCase().split(/[\s,;]+/).filter(Boolean))];
const REGION_LABEL: Record<string, string> = { AFRICA: 'Africa', AMERICAS: 'Americas', ASIA: 'Asia', EUROPE: 'Europe', OCEANIA: 'Oceania', ANTARCTICA: 'Antarctica' };

export function CountryPicker({ value, onChange, testId }: { value: string; onChange: (v: string) => void; testId?: string }) {
  const codes = parse(value);
  const bad = codes.filter((c) => !COUNTRY_CODES.has(c));
  const set = (next: string[]) => onChange([...new Set(next)].join(' '));
  const byRegion = new Map<string, number>();
  for (const c of codes) {
    const r = countryOf(c);
    if (r) byRegion.set(r.region, (byRegion.get(r.region) ?? 0) + 1);
  }
  const eu = codes.filter((c) => countryOf(c)?.eu).length;
  return (
    <div className="cp">
      <input value={value} onChange={(e) => onChange(e.target.value)} placeholder="TR DE" data-testid={testId} />
      <div className="cp-groups" aria-label="Add a region">
        {Object.entries(COUNTRY_GROUPS).map(([k, g]) => {
          const all = g.codes.every((c) => codes.includes(c));
          return (
            <button key={k} type="button" className={`cp-chip ${all ? 'on' : ''}`} title={all ? `Remove ${g.label}` : `Add ${g.codes.length} countries`} onClick={() => set(all ? codes.filter((c) => !g.codes.includes(c)) : [...codes, ...g.codes])}>
              {all ? '✓ ' : '+ '}
              {g.label}
            </button>
          );
        })}
        {codes.length > 0 && (
          <button type="button" className="cp-chip" onClick={() => onChange('')}>
            Clear
          </button>
        )}
      </div>
      <p className="cp-sum">
        {codes.length === 0 ? (
          'No country: nobody can be launched on this site.'
        ) : (
          <>
            <b>{codes.length - bad.length}</b> countr{codes.length - bad.length === 1 ? 'y' : 'ies'}
            {[...byRegion].map(([r, n]) => ` · ${REGION_LABEL[r]} ${n}`).join('')}
            {eu ? ` (EU ${eu})` : ''}
            {codes.length <= 12 && bad.length === 0 && <span className="muted"> — {codes.map(countryName).join(', ')}</span>}
          </>
        )}
        {bad.length > 0 && <span className="cp-bad"> Unknown code{bad.length === 1 ? '' : 's'}: {bad.join(' ')}</span>}
      </p>
    </div>
  );
}
